import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './lc-accessibility-widget.scss'

interface LCAccessibilityWidgetProps {
  customStyle?: React.CSSProperties
}

declare global {
  interface Window {
    UserWay?: {
      widgetOpen?: () => void
      widgetClose?: () => void
      widgetToggle?: () => void
      iconVisibilityOff?: () => void
      [key: string]: unknown
    }
  }
}

export const LCAccessibilityWidget = ({ customStyle }: LCAccessibilityWidgetProps): JSX.Element => {
  const { t } = useTranslation('e-commander-common', { keyPrefix: 'common' })
  const [isOpening, setIsOpening] = useState(false)
  const [isWidgetOpen, setIsWidgetOpen] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const pendingOpenRef = useRef(false)
  const hideNativeIcon = () => window.UserWay?.iconVisibilityOff?.()

  useEffect(() => {
    const scriptSrc = 'https://cdn.userway.org/widget.js'
    let script = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`)
    const currentLanguage = localStorage.getItem('currentLanguage') || 'en'

    if (!script) {
      script = document.createElement('script')
      script.src = scriptSrc
      script.async = true
      script.setAttribute('data-account', 'ZRJXcFSrYp')
      script.setAttribute('data-language', currentLanguage)
      document.head.appendChild(script)
    } else if (window.UserWay) {
      hideNativeIcon()
      setIsReady(true)
    }

    const handleInitCompleted = () => {
      hideNativeIcon()
      setIsReady(true)
      if (pendingOpenRef.current && window.UserWay?.widgetOpen) {
        window.UserWay.widgetOpen()
      }
      pendingOpenRef.current = false
      setIsOpening(false)
    }

    document.addEventListener('userway:init_completed', handleInitCompleted as EventListener)

    return () => {
      document.removeEventListener('userway:init_completed', handleInitCompleted as EventListener)
    }
  }, [])

  useEffect(() => {
    const handleWidgetOpen = () => {
      setIsWidgetOpen(true)
    }

    const handleWidgetClose = () => {
      setIsWidgetOpen(false)
    }

    window.addEventListener('AccessibilityOnMenuOpen', handleWidgetOpen)
    window.addEventListener('AccessibilityOnMenuClose', handleWidgetClose)

    return () => {
      window.removeEventListener('AccessibilityOnMenuOpen', handleWidgetOpen)
      window.removeEventListener('AccessibilityOnMenuClose', handleWidgetClose)
    }
  }, [])

  const handleWidgetClick = () => {
    hideNativeIcon()

    if (!window.UserWay || !isReady) {
      pendingOpenRef.current = true
      setIsOpening(true)
      return
    }

    setIsOpening(true)

    const userWay = window.UserWay
    const canToggle = typeof userWay?.widgetToggle === 'function'
    const canOpen = typeof userWay?.widgetOpen === 'function'
    const canClose = typeof userWay?.widgetClose === 'function'

    if (isWidgetOpen && canClose && userWay) {
      userWay.widgetClose?.()
    } else if (!isWidgetOpen && canOpen && userWay) {
      userWay.widgetOpen?.()
    } else if (canToggle && userWay) {
      userWay.widgetToggle?.()
    }

    setIsOpening(false)
  }

  return (
    <button
      id="accessibilityWidgetLCCommander"
      tabIndex={0}
      aria-label={t('accessibilityOptions')}
      title={t('accessibilityOptions')}
      aria-pressed={isWidgetOpen}
      className={`accessibility-widget ${isOpening ? 'loading' : ''} ${isWidgetOpen ? 'open' : ''}`}
      onClick={handleWidgetClick}
      style={customStyle}>
      {isOpening ? (
        <div className="loading-spinner">
          <i className="fa-solid fa-circle-notch fa-spin"></i>
        </div>
      ) : (
        <i className="fa-solid fa-universal-access"></i>
      )}
    </button>
  )
}
